import React from 'react';
import PropTypes from 'prop-types';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';

import computed from 'lib/mobx/computed';
import Localizable from 'lib/intl/localizable';
import Styled from 'styled';
import withDialog from 'lib/alerts/with-dialog';
import FormattedDateAgoView from 'lib/intl/formatted-date-ago-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';

import notification from 'four-nets/icons/svg/icon_notifications.svg';
import like from 'four-nets/icons/svg/icon_like.svg';
import message from 'four-nets/icons/svg/icon_comment.svg';
import users from 'four-nets/icons/svg/icon_users.svg';
import IconBoxView from 'four-nets/ui/icon-box/icon-box-view';
import BoxView from 'four-nets/ui/box-view';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import ClickableIconBoxView from 'four-nets/ui/clickable-icon-box/clickable-icon-box-view';
import UserGridListStackView from 'four-nets/user/user-grid-list-stack-view';
import { UserGridListViewModel } from 'four-nets/user/user-grid-list-view';
import defaultStyles from './thread-status-bar-view.scss';

export class ThreadStatusBarViewModel {
    thread: Thread;

    @observable userGridListViewModel;
    toggleNotificationsPromise: ?Promise<any>;

    constructor(options) {
        const { thread } = options;
        this.thread = thread;

        this.likersUserGridListViewModel = new UserGridListViewModel({
            loadUsers: () => this.thread.loadLikes(),
            extractUsers: like => like.liker,
        });

        this.membersUserGridListViewModel = new UserGridListViewModel({
            loadUsers: () => this.thread.loadMembers(),
            extractUsers: member => member,
        });
    }

    @computed get title() {
        if (this.userGridListViewModel === this.likersUserGridListViewModel) {
            return 'LIKE';
        }
        if (this.userGridListViewModel === this.membersUserGridListViewModel) {
            return 'THREAD_INVOLVED_USERS';
        }
        return null;
    }

    @action selectLikers() {
        this.userGridListViewModel = this.likersUserGridListViewModel;
    }

    @action selectMembers() {
        this.userGridListViewModel = this.membersUserGridListViewModel;
    }
}

@withDialog
@Localizable()
@Styled(defaultStyles)
@observer
class ThreadStatusBarView extends React.Component {
    toggleNotificationsPromise: ?Promise<any>;
    userGridListStackView: ?UserGridListStackView;

    state = { open: false };

    static contextTypes = {
        authCheck: PropTypes.func,
    };

    @action showLikers = () => {
        this.props.threadStatusBarViewModel.selectLikers();
        this.setOpen(true);
    };

    @action showMembers = () => {
        this.props.threadStatusBarViewModel.selectMembers();
        this.setOpen(true);
    };

    setOpen = value => {
        this.setState({ open: value });
    };

    toggleLocked = () => {
        return this.props.threadStatusBarViewModel.thread.toggleLocked().catch(alert);
    };

    toggleNotifications = () => {
        this.toggleNotificationsPromise = this.context.authCheck(
            this.props.threadStatusBarViewModel.thread.toggleNotifications,
            AuthOrigin.Notifications
        );
        return this.toggleNotificationsPromise;
    };

    componentWillUnmount() {
        if (this.toggleNotificationsPromise) {
            this.toggleNotificationsPromise.cancel();
        }
    }

    render() {
        const { styles, threadStatusBarViewModel } = this.props;
        const { thread, title, userGridListViewModel } = threadStatusBarViewModel;

        return (
            <div className={styles.threadStatusBar}>
                <div>
                    <span className={styles.box}>
                        <BoxView key='date'>
                            <FormattedDateAgoView value={thread.lastMessage.time} />
                        </BoxView>
                    </span>
                    <span className={styles.messagesCount}>
                        <IconBoxView
                            key='messagesCount'
                            icon={<SVGIcon style={{ image: styles.icon }} src={message} />}
                        >
                            {thread.replyCount}
                        </IconBoxView>
                    </span>
                    <span className={styles.likes}>
                        {thread.firstMessage.likesCount > 0 ? (
                            <ClickableIconBoxView
                                key='likesCount'
                                icon={<SVGIcon style={{ image: styles.icon }} src={like} />}
                                onClick={this.showLikers}
                                hoverable={true}
                            >
                                {thread.firstMessage.likesCount}
                            </ClickableIconBoxView>
                        ) : (
                            <IconBoxView key='likesCount' icon={<SVGIcon style={{ image: styles.icon }} src={like} />}>
                                {thread.firstMessage.likesCount}
                            </IconBoxView>
                        )}
                    </span>
                    <span className={styles.usersCount}>
                        {thread.usersCount > 0 ? (
                            <ClickableIconBoxView
                                key='usersCount'
                                icon={<SVGIcon style={{ image: styles.icon }} src={users} />}
                                onClick={this.showMembers}
                                hoverable={true}
                            >
                                {thread.usersCount}
                            </ClickableIconBoxView>
                        ) : (
                            <IconBoxView key='usersCount' icon={<SVGIcon style={{ image: styles.icon }} src={users} />}>
                                {thread.usersCount}
                            </IconBoxView>
                        )}
                    </span>
                </div>
                {/* observer doesn't work well with render props, that's why there is duplicated component */}
                {thread.isLocked ? (
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer && viewer.isSuperstaff ? (
                                <div className={styles.notificationsButton}>
                                    <ActionButtonView onClick={this.toggleLocked}>
                                        <FormattedMessage id='UNLOCK' />
                                    </ActionButtonView>
                                </div>
                            ) : null
                        }
                    </ViewerConsumer>
                ) : (
                    <ViewerConsumer>
                        {({ viewer }) =>
                            viewer && viewer.isSuperstaff ? (
                                <div className={styles.notificationsButton}>
                                    <ActionButtonView onClick={this.toggleLocked}>
                                        <FormattedMessage id='LOCK' />
                                    </ActionButtonView>
                                </div>
                            ) : null
                        }
                    </ViewerConsumer>
                )}

                <div className={styles.notificationsButton}>
                    <ActionButtonView
                        icon={<SVGIcon style={{ image: styles.icon }} src={notification} />}
                        onClick={this.toggleNotifications}
                    >
                        {thread.subscribed ? (
                            <FormattedMessage id='DISABLE_NOTIFICATIONS' />
                        ) : (
                            <FormattedMessage id='ENABLE_NOTIFICATIONS' />
                        )}
                    </ActionButtonView>
                </div>
                <UserGridListStackView
                    open={this.state.open}
                    title={
                        title === 'THREAD_INVOLVED_USERS' ? (
                            <FormattedMessage id='THREAD_INVOLVED_USERS' />
                        ) : (
                            <FormattedMessage id='LIKE' />
                        )
                    }
                    onClose={() => this.setOpen(false)}
                    userGridListViewModel={userGridListViewModel}
                />
            </div>
        );
    }
}

export default ThreadStatusBarView;
